import { Store } from 'pullstate'
import { WidgetZod, Widgets, registry } from './registry'
import { updateWidget, createWidget } from './api'
import { createContext, ReactNode, useMemo, useEffect, useContext } from 'react'
import React from 'react'

type EditMode = 'options' | 'reorder' | 'add' | 'remove'

type WidgetEditorStoreType = {
  pageId: number

  // Currently edited widget config
  editConfig: Widgets | null

  // Edit mode
  mode: EditMode
  isSaving: boolean

  // Configs for all widgets on the page
  config: {
    [id: string]: Widgets
  }
}

function createWidgetStore(
  initialConfig: { [key: string]: Widgets },
  pageId: number
) {
  return new Store<WidgetEditorStoreType>({
    pageId,
    editConfig: null,
    mode: 'options',
    isSaving: false,

    config: initialConfig,
  })
}

type EditorStoreType = ReturnType<typeof createWidgetStore>

const WidgetStoreContext = createContext<null | EditorStoreType>(null)

export function WidgetEditorProvider({
  children,
  initialConfig,
  pageId,
}: {
  children: ReactNode
  initialConfig: { [key: string]: Widgets }
  pageId: number
}) {
  // Create store only once, I think react-query was recreating the store
  const store = useMemo(() => {
    return createWidgetStore(initialConfig, pageId)
  }, [])

  useEffect(() => {
    // this still happens on every config change
    store.update((s) => {
      s.config = initialConfig
      s.pageId = pageId
    })
  }, [store, initialConfig, pageId])

  return (
    <WidgetStoreContext.Provider value={store}>
      {children}
    </WidgetStoreContext.Provider>
  )
}

function useWidgetStore() {
  const store = useContext(WidgetStoreContext)
  if (!store) {
    throw new Error('useWidgetStore must be used within a WidgetStoreProvider')
  }
  return store
}

export function useWidgetEditor() {
  const store = useWidgetStore()
  return new WidgetEditor(store)
}

class WidgetEditor {
  private store: EditorStoreType

  constructor(store: EditorStoreType) {
    this.store = store
  }

  // Hooks

  useMode = () => {
    return this.store.useState((s) => s.mode, [])
  }

  useEditWidgetId = () => {
    return this.store.useState((s) => s.editConfig?.id, []) || null
  }

  useEditCallback = (widgetId: string, mode: EditMode) => {
    return React.useCallback(() => {
      this.startEditWidget(widgetId, mode)
    }, [widgetId, mode, this.startEditWidget])
  }

  useChildren = (widgetId: string) => {
    return this.store.useState(
      (s) =>
        s.editConfig?.id === widgetId
          ? s.editConfig.children
          : s.config[widgetId]?.children || [],
      [widgetId]
    )
  }

  useConfig = (widgetId: string) => {
    return this.store.useState((s) => s.config[widgetId], [widgetId])
  }

  useEditConfig = () => {
    return this.store.useState((s) => s.editConfig, [])
  }

  useIsEditMode = (widgetId: string | null, mode: string | null) => {
    return this.store.useState(
      (s) =>
        s.editConfig != null &&
        (s.editConfig?.id === widgetId || widgetId == null) &&
        (s.mode === mode || mode == null),
      [widgetId, mode]
    )
  }

  useIsSaving = () => {
    return this.store.useState((s) => s.isSaving, [])
  }

  // Actions

  startEditWidget = (widgetId: string, mode: EditMode) => {
    this.store.update((s) => {
      if (s.editConfig?.id === widgetId && mode === 'options') {
        s.editConfig = null
        return
      }

      s.editConfig = s.config[widgetId]
      s.mode = mode
    })
  }

  endEditWidget = () => {
    this.store.update((s) => {
      s.editConfig = null
    })
  }

  setOrder = (children: string[]) => {
    this.store.update((s) => {
      if (s.editConfig) s.editConfig.children = children
    })
  }

  setOptions = (options: unknown) => {
    this.store.update((s) => {
      if (s.editConfig == null) return

      // Use zod to avoid `any` type and make sure that the values match the expected type
      const config = {
        ...s.editConfig,
        options: structuredClone(options),
      }
      const zodVerified = WidgetZod.safeParse(config)
      console.log(options, zodVerified)

      if (zodVerified.success) {
        s.editConfig = zodVerified.data
      }
      // TODO what to do if validation fails?
    })
  }

  removeChild = (childId: string) => {
    this.store.update((s) => {
      if (s.editConfig == null) return
      s.editConfig.children = s.editConfig.children.filter(
        (id: string) => id !== childId
      )
    })
  }

  save = async () => {
    const state = this.store.getRawState()
    if (state.isSaving) return

    if (!state.editConfig) {
      console.error('Saving but no widget is being edited')
      return
    }

    this.store.update((s) => {
      s.isSaving = true
    })

    const config = state.editConfig

    try {
      await updateWidget(
        state.pageId,
        config.id,
        config.options,
        config.children
      )

      this.store.update((s) => {
        s.config[config.id].children = config.children
        s.config[config.id].options = config.options
        s.editConfig = null
        s.isSaving = false
      })
    } catch (error) {
      console.error('Error saving widget:', error)
      this.store.update((s) => {
        s.isSaving = false
      })
      // TODO handle error, maybe show a notification
    }
  }

  addChildWidget = async (widgetType: string) => {
    const state = this.store.getRawState()
    if (!state.editConfig) {
      console.error('Cannot add child widget: no widget is being edited')
      return
    }

    const registryEntry = registry(widgetType)
    if (!registryEntry) {
      console.error(`Widget type "${widgetType}" not found in registry`)
      return
    }

    const newId = `widget_${Date.now()}`
    const parsedConfig = WidgetZod.safeParse({
      widget: widgetType,
      id: newId,
      options: registryEntry.getInitialOptions(),
      children: [],
    })

    if (!parsedConfig.success) {
      console.error(
        `Failed to create widget config for type "${widgetType}":`,
        parsedConfig.error
      )
      return
    }

    const config: Widgets = parsedConfig.data

    const parentId = state.editConfig.id
    const currentChildren = state.editConfig.children || []

    this.store.update((s) => {
      s.isSaving = true
    })

    try {
      await createWidget(state.pageId, config)

      await updateWidget(state.pageId, parentId, state.editConfig!.options, [
        ...currentChildren,
        newId,
      ])

      this.store.update((s) => {
        s.config[newId] = config

        if (s.editConfig && s.editConfig.id === parentId) {
          s.editConfig.children = [...currentChildren, newId]
        }
        s.config[parentId].children = [...currentChildren, newId]

        s.editConfig = null
        s.isSaving = false
      })
    } catch (error) {
      console.error('Error adding child widget:', error)
      this.store.update((s) => {
        s.isSaving = false
      })
      // TODO handle error, maybe show a notification
    }
  }
}
